/**
 * Welcome popup for first-time visitors, offering the free Career Discovery Quiz.
 * Shown once per visitor (localStorage), after a short delay, never to signed-in users.
 * Calm, no urgency language (HDI Principle 19).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { track } from "@/lib/analytics";

const KEY = "wekit.freebie-popup.v1";

const CHAT = [
  { me: true, text: "What career actually suits me?", time: "11:24" },
  { me: false, text: "You lean Builder + Thinker. Here are 3 directions to explore.", time: "11:25" },
  { me: true, text: "That really helps. Thank you! 🙏", time: "11:26" },
];

export default function FreebiePopup() {
  const { user } = useAuth();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user) return;
    try { if (localStorage.getItem(KEY)) return; } catch { return; }
    const t = setTimeout(() => {
      setOpen(true);
      track("freebie_popup_view", { surface: "home" });
    }, 4000);
    return () => clearTimeout(t);
  }, [user]);

  const close = (next: boolean) => {
    setOpen(next);
    if (!next) try { localStorage.setItem(KEY, String(Date.now())); } catch { /* ignore */ }
  };

  const bubble = (i: number) =>
    reduce ? {} : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.3 + i * 0.35 } };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-[min(56rem,calc(100vw-2rem))] overflow-hidden rounded-3xl border-primary/30 bg-card/95 p-0 backdrop-blur-xl">
        <div className="grid md:grid-cols-2">
          <div className="flex flex-col gap-4 p-8 sm:p-10">
            <span className="inline-flex items-center gap-2 font-display text-lg font-semibold">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/20 text-primary">
                <Compass className="h-5 w-5" aria-hidden="true" />
              </span>
              WeKIT™
            </span>
            <DialogTitle asChild>
              <h2 className="font-display leading-tight">
                <span className="block text-2xl font-medium sm:text-3xl">Your career snapshot</span>
                <span className="block text-6xl font-extrabold tracking-tight text-primary sm:text-7xl">FREE</span>
              </h2>
            </DialogTitle>
            <DialogDescription className="text-base leading-relaxed text-muted-foreground">
              8 quick choices. Discover your leading archetype and three career directions worth exploring.
            </DialogDescription>
            <Link
              to="/free/career-quiz?src=popup"
              onClick={() => { track("freebie_popup_click", { surface: "home" }); close(false); }}
              className="lift mt-2 inline-flex min-h-14 w-fit items-center gap-3 rounded-full bg-primary px-8 text-lg font-semibold text-primary-foreground shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Start free quiz <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
            <p className="text-xs text-muted-foreground">About 2 minutes · no card needed</p>
          </div>

          <div className="relative hidden flex-col justify-center gap-4 overflow-hidden bg-primary/5 p-8 md:flex" aria-hidden="true">
            <div className="pointer-events-none absolute -right-24 top-1/2 h-[28rem] w-[28rem] -translate-y-1/2 rounded-full border border-primary/20" />
            <div className="pointer-events-none absolute -right-8 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full border border-primary/15" />
            {CHAT.map((m, i) => (
              <motion.div key={i} {...bubble(i)} className={`relative flex items-end gap-2 ${m.me ? "justify-end" : "justify-start"}`}>
                {!m.me && (
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                    <Sparkles className="h-4 w-4" />
                  </span>
                )}
                <div className={`max-w-[80%] rounded-3xl px-5 py-3 text-sm shadow-md ${m.me ? "bg-background text-foreground" : "bg-primary text-primary-foreground"}`}>
                  <p>{m.text}</p>
                  <p className="mt-1 text-right text-[11px] opacity-70">{m.time}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
